import React, { memo, useMemo } from 'react';
import { FilterPanel, Spinner } from '@gsa/afp-component-library';
import { useQuery } from '@apollo/client';
import { GET_REQUISITIONS_STATUSES } from '../queries';
import AgencyFilterItem from './AgencyFilterItem';
import BureauFilterItem from './BureauFilterItem';
import OfficeFilterItem from './OfficeFilterItem';
import AssigneeFilterItem from './AssigneeFilterItem';
import {
  AGENCY_AND_BUREAU_AND_OFFICE,
  AGENCY_FILTER,
  BUREAU_FILTER,
  OFFICE_FILTER,
} from './filter-item-constants';
import {
  requisitionTypeDescriptor,
  leasingRequisitionTypeDescriptor
} from '../../non-standard-purchase/constants';
import {
  formatRequisitionStatusLabel,
  formatRequisitionTypeLabel,
} from '../../my-orders/Filters/OrderFilterHelpers';
import {
  FMC,
  LEASING_AGENCY_BUREAU_AND_OFFICE, LEASING_AGENCY_FILTER, LEASING_BUREAU_FILTER, LEASING_OFFICE_FILTER,
  ZONE,
  ZONE_AND_FMC
} from "../../my-orders/Filters/filter-item-constants.jsx";
import ZonesFilterItem from "../../my-orders/Filters/ZonesFilterItem.jsx";
import FMCFilterItem from "../../my-orders/Filters/FMCFilterItem.jsx";
import LeasingAgencyFilterItem from "../../my-orders/Filters/LeasingAgencyFilterItem.jsx";
import LeasingBureauFilterItem from "../../my-orders/Filters/LeasingBureauFilterItem.jsx";
import LeasingOfficeFilterItem from "../../my-orders/Filters/LeasingOfficeFilterItem.jsx";
import StandardItemFilterItem from "../../../filters/StandardItemFilterItem.jsx";
import { UserTypes } from "../../../constants/index.jsx";
import { useCurrentUser } from '@gsa/afp-shared-ui-utils';

const { FilterPanel: ComponentFilterPanel } = FilterPanel;

const MyRequisitionsFilters = ({ setFilters, allVehicleTypes }) => {
  const { currentUser } = useCurrentUser();
  // this is needed for finance officers who have the fleet base role and are not gsa employee
  const hasFleetBaseRole = currentUser?.roles?.some((role) => role?.id === '104');
  const internalUser = currentUser?.userType?.id === UserTypes.GSA_EMPLOYEE || hasFleetBaseRole;
  const RequisitionStatusOption = {
    key: 'requisition_status',
    title: 'Requisition status',
  };
  const VehicleTypeOption = {
    label: 'title',
    value: 'code',
    key: 'vehicle_type_code',
    title: 'Vehicle type',
  };
  const AgencyOption = {
    label: 'name',
    value: 'id',
    key: AGENCY_FILTER,
    title: 'Agency Item',
  };
  const BureausOption = {
    label: 'name',
    value: 'id',
    key: BUREAU_FILTER,
    title: 'Bureau Item',
  };
  const OfficesOption = {
    label: 'officeName',
    value: '',
    key: OFFICE_FILTER,
    title: 'Group Item',
  };
  const RequisitionTypeOption = {
    key: 'requisition_type',
    title: 'Requisition type',
  };
  const AssigneeOption = {
    key: 'assigned_engineer_id',
    title: 'Assignee',
  };
  const RequisitionTransactionTypeOption = {
    key: 'transaction_type',
    title: 'Transaction type',
  };
  const CloseoutDate = {
    key: 'order_end_date',
    title: 'Closeout date',
  };
  const StandardItemCode = {
    key: 'standard_item_code',
    title: 'SIN',
  };
  const FundingTypeOption = {
    key: 'funding_type',
    title: 'Funding type',
  };

  const { data: requisitionsStatusData, loading: loadingRequisitionStatuses } = useQuery(GET_REQUISITIONS_STATUSES, {
    fetchPolicy: 'no-cache',
  });

  // filters
  const allRequisitionTypes = Object.entries(requisitionTypeDescriptor).filter((x) => x[0] !== '32');
  const allFundingTypes = Object.entries(leasingRequisitionTypeDescriptor);
  const allRequisitionStatuses = useMemo(() => {
    if (!requisitionsStatusData) return [];
    return requisitionsStatusData?.getRequisitionStatuses || [];
  }, [requisitionsStatusData]);

  const purchasingFilters = [
    {
      key: 'friendly_name',
      label: 'Search requisition name',
      title: 'Requisition Name',
      type: 'text',
      operator: '$like',
      position: 'top',
      showSearchButton: true,
      hideClear: false,
      value: '',
    },
    {
      key: 'requisition_number',
      title: 'Requisition number',
      type: 'text',
      operator: '$like',
      value: '',
    },
    {
      key: RequisitionStatusOption.key,
      title: RequisitionStatusOption.title,
      type: 'multiselect',
      value: [],
      options: allRequisitionStatuses.map((status) => ({
        label: formatRequisitionStatusLabel(status),
        value: status,
      })),
      operator: '$in',
      hideClear: true,
    },
    {
      key: RequisitionTypeOption.key,
      title: RequisitionTypeOption.title,
      type: 'multiselect',
      value: [],
      options: allRequisitionTypes.map((type) => ({
        label: formatRequisitionTypeLabel(type[1]),
        value: type[0],
      })),
      operator: '$in',
      hideClear: true,
    },
    {
      key: RequisitionTransactionTypeOption.key,
      title: RequisitionTransactionTypeOption.title,
      type: 'multiselect',
      value: [],
      options: [
        { label: 'Purchase', value: 'VEHICLE_SALE' },
        { label: 'Lease', value: 'VEHICLE_LEASE' },
      ],
      operator: '$in',
      hideClear: true,
    },
    {
      key: AssigneeOption.key,
      title: AssigneeOption.title,
      type: 'multiselect',
      value: [],
      component: AssigneeFilterItem,
      operator: '$in',
      hideClear: true,
    },
    {
      key: VehicleTypeOption.key,
      title: VehicleTypeOption.title,
      type: 'multiselect',
      value: [],
      options: allVehicleTypes.map((vehicleType) => ({
        label: vehicleType[VehicleTypeOption.label],
        value: vehicleType[VehicleTypeOption.value],
      })),
      operator: '$in',
      hideClear: true,
    },
    {
      title: 'Agency',
      key: AGENCY_AND_BUREAU_AND_OFFICE,
      filters: [
        {
          key: AgencyOption.key,
          title: AgencyOption.title,
          component: AgencyFilterItem,
          permanent: false,
          operator: '$exact',
          value: '',
        },
        {
          key: BureausOption.key,
          title: BureausOption.title,
          component: BureauFilterItem,
          permanent: false,
          operator: '$in',
          placeholder: '- Select bureau -',
        },
        {
          key: OfficesOption.key,
          title: OfficesOption.title,
          component: OfficeFilterItem,
          permanent: false,
          operator: '$in',
          value: '',
          placeholder: '- Select group -',
        }
      ],
    },

    {
      key: StandardItemCode.key,
      id: 'filter-order-by-sin',
      title: StandardItemCode.title,
      permanent: false,
      component: StandardItemFilterItem(StandardItemCode.key),
      operator: '$exact',
      options: [],
      value: '',
    },
    {
      title: CloseoutDate.title,
      key: CloseoutDate.key,
      id: 'filter-order-by-date',
      type: 'daterange',
      operator: '$between',
      options: {
        startDate: {
          key: 'beginning',
          label: 'Start Date',
          hint: 'mm/dd/yyyy',
          maxDate: new Date('12/31/9999').toISOString(),
        },
        endDate: {
          key: 'end',
          label: 'End Date',
          hint: 'mm/dd/yyyy',
          maxDate: new Date('12/31/9999').toISOString(),
        },
      },
      value: {
        beginning: '',
        end: '',
      },
      hideClear: true,
    }
  ];

  const leasingFilters = [
    {
      title: 'Leasing Details',
      type: 'label',
    },
    {
      title: 'Zone and FMC',
      key: ZONE_AND_FMC,
      filters: [
        {
          key: ZONE,
          label: 'Zone',
          component: ZonesFilterItem,
          permanent: false,
          operator: '$exact',
          value: '',
          placeholder: '- Select -',
        },
        {
          key: FMC,
          label: 'FMC Name',
          component: FMCFilterItem,
          permanent: false,
          operator: '$exact',
          placeholder: '- Select -',
        },
      ],
    },
    {
      title: 'FSR',
      key: 'fsr_user_email_address',
      type: 'text',
      value: '',
      operator: '$like',
      hideClear: false,
      minSearchLength: 4,
      showSearchButton: true,
      permanent: false,
      position: 'top',
    },
    {
      title: 'Customer BOAC',
      key: 'boac',
      type: 'text',
      value: '',
      operator: '$like',
      hideClear: false,
      minSearchLength: 4,
      showSearchButton: true,
      permanent: false,
      position: 'top',
    },
    {
      title: 'Leasing Organization',
      key: LEASING_AGENCY_BUREAU_AND_OFFICE,
      filters: [
        {
          key: LEASING_AGENCY_FILTER,
          title: 'Leasing Agency',
          component: LeasingAgencyFilterItem,
          permanent: false,
          operator: '$exact',
          value: '',
          placeholder: '- Select agency -',
        },
        {
          key: LEASING_BUREAU_FILTER,
          title: 'Leasing bureau',
          component: LeasingBureauFilterItem,
          permanent: false,
          operator: '$in',
          placeholder: '- Select bureau -',
        },
        {
          key: LEASING_OFFICE_FILTER,
          title: 'Leasing office',
          component: LeasingOfficeFilterItem,
          permanent: false,
          operator: '$in',
          value: '',
          placeholder: '- Select group -',
        },
      ],
    },
    {
      key: FundingTypeOption.key,
      title: FundingTypeOption.title,
      type: 'multiselect',
      value: [],
      options: allFundingTypes.map((type) => ({
        label: formatRequisitionTypeLabel(type[1]),
        value: type[0],
      })),
      operator: '$in',
      hideClear: true,
    },
  ];

  const handleFiltersChange = (filters) => {
    const vehicleTypeFilter = filters.find((filter) => filter.key === VehicleTypeOption.key);
    // if the vehicle type filter changed, map it to the proper value
    if (vehicleTypeFilter) {
      const vehicleTypeValues = vehicleTypeFilter.value;
      const newVehicleTypes = allVehicleTypes.filter((item) =>
        vehicleTypeValues.includes(item[VehicleTypeOption.value])
      );
      vehicleTypeFilter.value = newVehicleTypes.map((item) => item[VehicleTypeOption.value]);

      if (vehicleTypeFilter.value.length === 0) {
        filters = filters.filter((filter) => filter.key !== VehicleTypeOption.key);
      }
    }

    const requisitionStatusFilter = filters.find((filter) => filter.key === RequisitionStatusOption.key);
    // if the requisition status filter changed, map it to the proper value
    if (requisitionStatusFilter) {
      let updatedValues = [...requisitionStatusFilter.value];
      if (updatedValues.includes('RECEIVED_BY_GSA')) {
        if (!updatedValues.includes('ORDER_RECEIVED')) updatedValues.push('ORDER_RECEIVED');
      } else {
        updatedValues = updatedValues.filter((item) => item !== 'ORDER_RECEIVED');
      }
      requisitionStatusFilter.value = updatedValues;

      if (requisitionStatusFilter.value.length === 0) {
        filters = filters.filter((filter) => filter.key !== RequisitionStatusOption.key);
      }
    }

    const closeOutDateFilter = filters.find((filter) => filter.key === CloseoutDate.key);
    if (closeOutDateFilter) {
      const updatedValues = Object.values(closeOutDateFilter.value);
      const beginningOfTime = new Date('01/01/1900').toISOString();
      const endOfTime = new Date('12/31/9999').toISOString();
      if (updatedValues[0] === '') updatedValues[0] = beginningOfTime;
      if (!updatedValues[1] || updatedValues[1] === '') updatedValues[1] = endOfTime;

      closeOutDateFilter.value = updatedValues.map((date) => new Date(date).toISOString());
      if (closeOutDateFilter.value.length === 0) {
        filters = filters.filter((filter) => filter.key !== CloseoutDate.key);
      }
    }

    setFilters(filters);
  };

  const filterStructure = useMemo(() => {
    return internalUser ? [...purchasingFilters, ...leasingFilters] : purchasingFilters; 
  }, [internalUser, purchasingFilters, leasingFilters]);

  if (loadingRequisitionStatuses) {
    return <Spinner />;
  }

  return (
    <ComponentFilterPanel
      clearButtonLabel="Reset All"
      filterStructure={filterStructure}
      model="Task"
      order={[]}
      setQueryFiltersState={handleFiltersChange}
      showClearIcon
    />
  );
};

export default MyRequisitionsFilters;
